import React, { useContext, useState, useEffect, useRef } from 'react';
import { Col, Row, Form, Button, Container, Card } from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css';
import { schema } from '../../schema';
import { Formik, FieldArray, getIn, FormikProps } from 'formik';
import "../../optional"; // custom "optional" function for yup validation
import modelContext from '../../modelContext';
import { CompartForm, Compartment } from './compart_types';
import '../CustomFormHandling/FormStyles.css';
import { getChannelCoords, getSphereCoords, getLayerCoords } from '../../findGeometryVoxels';


// functional component for the compartments tab of the forms. 
export default function FormComparts(props: any) {
	const [submitSuccess, setSubmitSuccess] = useState(false);
	const [touchedSubmit, setTouchedSubmit] = useState(false);
	useEffect(() => { }, [submitSuccess])
	useEffect(() => { }, [touchedSubmit])

	let emptyCompart = {
		_attributes: {
			id: "",
			name: "",
		},
		annotation: {
			"cell4d:latticePointDefinition": [
				{
					_attributes: {
						type: "solid",
						x1: "",
						y1: "",
						z1: "",
						x2: "",
						y2: "",
						z2: "",
					}
				}
			]
		}
	}

	let emptyProperties = {
		"_attributes": {
			"type": "membrane",
			"axis": "",
			"face": "",
			"membraneEmissionRate": "0",
			"absorptionRate": "0"
		}
	}

	const model = useContext(modelContext);
	// formik reference
	const formRef = useRef<FormikProps<CompartForm>>(null);
	const [initialValues, setInitialValues] = useState(props.initialValues);
	const [showCoords, setShowCoords] = useState(Array<Boolean>());

	// This function handles overwriting existing forms by uploading a new form. it clears the existing one first, then replaces the blank from with new data
	// the setTimeout is a workaround because you cannot listen for changes made to setFieldValue, which is async.
	// the overwrite is needed because the checkboxes that toggle membrane field options can be stale when updated from upload, so everything needs to be cleared.
	// fix in the future if possible, but no big deal
	const handleFieldOverwrite = () => {
		if (formRef?.current) {
			formRef?.current.setFieldValue(`compartment`, []);
			setTimeout(() => {
				formRef?.current?.setFieldValue(`compartment`, model.modelJson["listOfCompartments"].compartment);
			}, 100)
			//
			let showCoordsArray = Array(model.modelJson["listOfCompartments"].compartment.length).fill(false);
			// automatically hide inaccessible compartment coordinates
			for (let i = 0; i < model.modelJson["listOfCompartments"]["compartment"].length; i++) {
				if (["000","999"].includes(model.modelJson["listOfCompartments"]["compartment"][i]._attributes.name)) {
					showCoordsArray[i] = false;
				}
			}
			setShowCoords(showCoordsArray);
		}
	}

	useEffect(() => {
		setInitialValues(props.initialValues);
		handleFieldOverwrite();
	}, [props.initialValues])

	// manage the showCoords array when comparts are added/removed
	const handleCompartCoordsDisplay = (index?: number) => {
		let new_coords = showCoords;
		if (!!index || index === 0) {
			// delete selected array element
			new_coords = [...showCoords.slice(0, index), ...showCoords.slice(index + 1)];
		} else {
			// insert true at second last slot
			new_coords = [...showCoords.slice(0, -1), true, ...showCoords.slice(-1)];
		}
		setShowCoords(new_coords);
	}

	const handleCompartIDSwap = (index1: number, index2: number) => {
		if(formRef?.current?.values) {
			let val1 = formRef.current.values.compartment[index1]._attributes.name;
			let val2 = formRef.current.values.compartment[index2]._attributes.name;
			// because this function is called AFTER arrayHelper.swap is called, but presumably BEFORE the swap actually happens (async?), I can grab the indices before the swap
			// then use the old indices to replace the same positions after the swap to ensure that the indices stay in place when the compartments are switched
			// setFieldValue is also likely async
			formRef.current.setFieldValue(`compartment.[${index1}]._attributes.name`, val1);
			formRef.current.setFieldValue(`compartment.[${index2}]._attributes.name`, val2);
		}
	}

	const errorsMap = (error_string: string): string => {
		let return_string = "";
		if (error_string === "missing_var") {
			return_string = "This field cannot be empty."
		} else if (error_string === "invalid_var") {
			return_string = "This field is invalid."
		}
		return (return_string);
	}

	const showCoordErrors = (input_string: string): string => {
		let errorsList: Array<string> = [];
		let errorMsg = "";
		if (formRef?.current) {
			let errorObj = getIn(formRef?.current.errors, input_string);
			let touchedObj = {};
			if (formRef?.current.touched) {
				touchedObj = getIn(formRef?.current.touched, input_string);
			}
			if (errorObj && touchedObj) {
				errorMsg = "Coordinates cannot be empty.";
				errorsList = Object.values(errorObj);
				errorsList.forEach((error: string) => {
					if (error === "invalid_var") {
						errorMsg = "Coordinates must be 0 or a positive integer.";
					}
				})
			}

		}
		return (errorMsg);
	}


	// if non-cube geometry is selected, handle the inaccessible space determination whenever the dimensions or geometry shape is changed
	useEffect(() => {
		// if custom, make sure there is an inaccessible space compartment. if not, add it.
		if (model.modelJson['cell4d:environmentVariables']["cell4d:GEOMETRY"]._text === "custom") {
			let inaccessibleCompart: Compartment = JSON.parse(JSON.stringify(emptyCompart));
			inaccessibleCompart._attributes.id = "inaccessible";
			if (formRef?.current) {
				// replace inaccessible if found, add to array index 0 if not found
				let found = false;
				for (let compartIndex = 0; compartIndex < formRef.current.values.compartment.length; compartIndex++) {
					if (formRef.current.values.compartment[compartIndex]._attributes.id === "inaccessible") {
						found = true;
						break;
					}
				}
				if (!found) {
					formRef.current.setFieldValue(`compartment`, [inaccessibleCompart, ...formRef.current.values.compartment]);
				}
			}
		}

		// if shape is cube, remove the inaccessible space from form and saved values
		if (model.modelJson['cell4d:environmentVariables']["cell4d:GEOMETRY"]._text === "cube") {
			if (formRef?.current) {
				// remove inaccessible if found
				for (let compartIndex = 0; compartIndex < formRef.current.values.compartment.length; compartIndex++) {
					if (formRef.current.values.compartment[compartIndex]._attributes.id === "inaccessible") {
						formRef.current.setFieldValue(`compartment`, [...formRef.current.values.compartment.slice(0, compartIndex), ...formRef.current.values.compartment.slice(compartIndex + 1)]);

						let new_showCoords = [...showCoords.slice(0, compartIndex), ...showCoords.slice(compartIndex + 1)];
						break;
					}
				}
			}
			if (model?.modelJson?.listOfCompartments?.compartment) {
				for (let compartIndex = 0; compartIndex < model.modelJson.listOfCompartments.compartment.length; compartIndex++) {
					if (model.modelJson.listOfCompartments.compartment[compartIndex]._attributes.id === "inaccessible") {
						model.appendModelJson("listOfCompartments", {
							compartment: [...model.modelJson.listOfCompartments.compartment.slice(0, compartIndex),
							...model.modelJson.listOfCompartments.compartment.slice(compartIndex + 1)]
						})
						break;
					}
				}
			}
		}

		// if sphere, generate a sphere with radius equal to shortest axis, then place it in the center of the simulation space
		// the sphere is a negative space generated by carving away corner voxels until the remaining voxels approximate a sphere
		// if channel, carve out a channel in the middle of the simulation space along longest axis, xDim by default if all equal

		// if an inaccessible compartment already exists, it will be overwritten by the new coordinates
		// if it doesn't exist, that compartment will be generated. This is done to the form as well as the saved data
		if (model.modelJson['cell4d:environmentVariables']["cell4d:GEOMETRY"]._text === "sphere" ||
			model.modelJson['cell4d:environmentVariables']["cell4d:GEOMETRY"]._text === "channel" ||
			model.modelJson['cell4d:environmentVariables']["cell4d:GEOMETRY"]._text === "layer") {
			let xDim = model.modelJson['cell4d:environmentVariables']['cell4d:X_DIM']._text;
			let yDim = model.modelJson['cell4d:environmentVariables']['cell4d:Y_DIM']._text;
			let zDim = model.modelJson['cell4d:environmentVariables']['cell4d:Z_DIM']._text;

			let inaccessibleCoords: Array<Array<number>> = [];
			if (model.modelJson['cell4d:environmentVariables']["cell4d:GEOMETRY"]._text === "sphere") {
				// xdim, ydim, zdim, radius. radius is the smallest of the 3 dimensions
				let radius = (Math.min(xDim, yDim, zDim) / 2) - 0.4;
				inaccessibleCoords = getSphereCoords(xDim, yDim, zDim, radius);
			} else if (model.modelJson['cell4d:environmentVariables']["cell4d:GEOMETRY"]._text === "channel") {
				inaccessibleCoords = getChannelCoords(xDim, yDim, zDim);
			} else if (model.modelJson['cell4d:environmentVariables']["cell4d:GEOMETRY"]._text === "layer") {
				inaccessibleCoords = getLayerCoords(xDim, yDim, zDim);
			}

			let inaccessibleCompart: Compartment = JSON.parse(JSON.stringify(emptyCompart));
			inaccessibleCompart._attributes.name = "000";
			inaccessibleCompart._attributes.id = "inaccessible";
			inaccessibleCompart.annotation['cell4d:latticePointDefinition'] = inaccessibleCoords.map((coords: Array<number>) => {
				return (
					{
						_attributes: {
							type: "solid",
							x1: coords[0],
							y1: coords[1],
							z1: coords[2],
							x2: coords[3],
							y2: coords[4],
							z2: coords[5],
						}
					}
				)
			});
			// add the inaccessible comparts to the current form
			if (formRef?.current) {
				// replace inaccessible if found, add to array index 0 if not found
				let found = false;
				let new_showCoords = Array(formRef.current.values.compartment.length).fill(true);
				for (let compartIndex = 0; compartIndex < formRef.current.values.compartment.length; compartIndex++) {
					if (formRef.current.values.compartment[compartIndex]._attributes.id === "inaccessible") {
						found = true;
						formRef.current.values.compartment[compartIndex].annotation = inaccessibleCompart.annotation;

						new_showCoords[compartIndex] = false;
						// setShowCoords(new_showCoords);
						break;
					}
				}
				if (!found) {
					formRef.current.setFieldValue(`compartment`, [inaccessibleCompart, ...formRef.current.values.compartment]);
					new_showCoords.push(true);
					new_showCoords[0] = false;
					// setShowCoords(new_showCoords);
				}
			}
			// do the same to the saved compart model
			let found = false;
			for (let compartIndex = 0; compartIndex < model.modelJson.listOfCompartments.compartment.length; compartIndex++) {
				if (model.modelJson.listOfCompartments.compartment[compartIndex]._attributes.id === "inaccessible") {
					found = true;
					model.modelJson.listOfCompartments.compartment[compartIndex].annotation = inaccessibleCompart.annotation;

					break;
				}
			}
			if (!found) {
				model.appendModelJson("listOfCompartments", { compartment: [inaccessibleCompart, ...model.modelJson.listOfCompartments.compartment] })
			}
		}


	},
		[model.modelJson['cell4d:environmentVariables']["cell4d:X_DIM"]._text,
		model.modelJson['cell4d:environmentVariables']["cell4d:Y_DIM"]._text,
		model.modelJson['cell4d:environmentVariables']["cell4d:Z_DIM"]._text,
		model.modelJson['cell4d:environmentVariables']["cell4d:GEOMETRY"]._text])

	const handleSubmit = (values: any) => {
		let comparts_out = {
			compartment: values.compartment
		}

		// Check to see if the submitted compartment list will break the existing model. if so, create warning. if ignored, set broken tabs to false, disable save model until fixed.
		let temp_compartList: Array<string> = values.compartment.map((compart: any, index: number) => {
			let id: string = compart._attributes.id;
			return (id);
		});
		// validateModel input order is comparts, annot, metabs, species
		let temp_modelStatus: Array<Boolean> = model.validateModel(temp_compartList);
		// because handleSubmit was successfully called, new compartment tab is correct for sure, so set it to true.
		temp_modelStatus[1] = true;

		if (!temp_modelStatus.every(tab => tab === true)) {
			let error_string = "";
			if (!temp_modelStatus[2]) error_string += "\r\nBase species";
			if (!temp_modelStatus[3]) error_string += "\r\nMolecules";
			if (!temp_modelStatus[4]) error_string += "\r\nReactions";
			if (!temp_modelStatus[5]) error_string += "\r\nEvents";
			let confirmed = window.confirm("This new compartment list will introduce errors in the following tabs: " + error_string +
				"\r\nAre you sure? Save model button will be disabled until all model tabs are valid again.");
			if (!confirmed) return;
		}
		model.setModelStatus(temp_modelStatus);

		model.appendModelJson("listOfCompartments", comparts_out);
		model.updateComparts(values.compartment);
		// console.log(JSON.stringify(emptyCompart));
		setSubmitSuccess(true);
	}

	return (
		<div>
			<Formik
				innerRef={formRef}
				validationSchema={schema.compart}
				onSubmit={handleSubmit}
				enableReinitialize={false}
				initialValues={initialValues} >
				{({
					setFieldValue,
					handleSubmit,
					handleChange,
					handleBlur,
					values,
					touched,
					errors,
				}) => (
					<Form onSubmit={handleSubmit} noValidate autoComplete="off">
						<Container>
							<Row className="justify-content-md-center"><Col sm={11}>
								{/* FieldArray and arrayHelpers help handle forms that are structured as arrays: each compartment is an element within the compartment array */}
								<FieldArray name="compartment" render={arrayHelpers => {
									return (
										<div> {
											// Ternary statement here ensures the compartment array exists and is not empty before trying to render its elements
											values?.compartment && values?.compartment.length > 0 ? values.compartment.map((compart: any, index: number) => (
												<div key={index}>
													<Card >
														<Row style={{ paddingTop: 10 }}>
															{/* Move order of compartments */}
															<Col xs={1} className="align-self-center" style={{ paddingLeft: 10, paddingRight: 10 }}>
																{/* Don't show if already first element, or if inaccessible/background is this or 1 above */}
																{index === 0 || ["000", "999"].includes(values.compartment[index]._attributes.name) ||
																["000", "999"].includes(values?.compartment?.[index - 1]?._attributes?.name) ? null :
																	<Button variant="outline-dark" size="sm" onClick={() => {
																		arrayHelpers.swap(index, index - 1);
																		handleCompartIDSwap(index, index - 1);
																	}}>↑</Button>
																}
																<br /><br />
																{/* Don't show if already last element, or if inaccessible/background is this or 1 below */}
																{index === values?.compartment.length - 1 || ["000", "999"].includes(values.compartment[index]._attributes.name) ||
																["000", "999"].includes(values?.compartment?.[index + 1]?._attributes?.name) ? null :
																	<Button variant="outline-dark" size="sm" onClick={() => {
																		arrayHelpers.swap(index, index + 1);
																		handleCompartIDSwap(index, index + 1);
																	}}>↓</Button>
																}
															</Col>
															<Col xs={3} style={{ paddingLeft: 0 }}>
																<Button size="sm" variant="outline-secondary" onClick={() => {
																	let newCoords = [...showCoords];
																	newCoords[index] = !newCoords[index];
																	setShowCoords(newCoords);
																}}>{showCoords[index] ? "Hide coordinates" : "Show coordinates"}</Button>
																<br />
																<Form.Label>Compartment name</Form.Label>
																<Form.Control className="form-control" size="sm"
																	name={`compartment.[${index}]._attributes.id`}
																	value={values.compartment[index]._attributes.id}
																	onChange={handleChange}
																	onBlur={handleBlur}
																	isValid={getIn(touched, `compartment.[${index}]._attributes.id`) &&
																		!getIn(errors, `compartment.[${index}]._attributes.id`)}
																	isInvalid={getIn(touched, `compartment.[${index}]._attributes.id`) &&
																		!!getIn(errors, `compartment.[${index}]._attributes.id`)} />
																<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `compartment.[${index}]._attributes.id`))}</Form.Control.Feedback>
																{/* don't allow preset background/inaccessible comparts to be membranes */}
																{values.compartment[index]._attributes.name === "999" || values.compartment[index]._attributes.name === "000" ? null :
																	<Form.Check type="checkbox" label="Membrane compartment"
																		checked={values?.compartment?.[index]?.annotation?.["cell4d:compartmentProperties"]}
																		onBlur={handleBlur}
																		onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																			handleChange(e);
																			let compart_annot = JSON.parse(JSON.stringify(values?.compartment?.[index]?.annotation));
																			if (e.target.checked) {
																				compart_annot["cell4d:compartmentProperties"] = JSON.parse(JSON.stringify(emptyProperties));
																			} else {
																				delete compart_annot["cell4d:compartmentProperties"];
																			}
																			setFieldValue(`compartment.[${index}].annotation`, compart_annot);
																		}} />
																}
																<br />
																{values?.compartment?.[index]?.annotation?.["cell4d:compartmentProperties"] ?
																	<>
																		<Form.Label>Membrane axis</Form.Label>
																		<Form.Control as="select" custom
																			id="dropdown-basic-button"
																			size="sm"
																			name={`compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.axis`}
																			value={values.compartment[index].annotation["cell4d:compartmentProperties"]._attributes.axis}
																			onChange={handleChange}
																			onBlur={handleBlur}
																		>
																			{!values?.compartment?.[index]?.annotation?.["cell4d:compartmentProperties"]?._attributes?.axis ?
																				<option value="">choose option</option>
																				: null}
																			<option value="x">X-axis</option>
																			<option value="y">Y-axis</option>
																			<option value="z">Z-axis</option>
																		</Form.Control>
																		<br />
																		<br />
																		<Form.Label>Membrane face</Form.Label>
																		<Form.Control as="select" custom
																			id="dropdown-basic-button"
																			size="sm"
																			name={`compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.face`}
																			value={values.compartment[index].annotation["cell4d:compartmentProperties"]._attributes.face}
																			onChange={handleChange}
																			onBlur={handleBlur}
																		>
																			{!values?.compartment?.[index]?.annotation?.["cell4d:compartmentProperties"]?._attributes?.face ?
																				<option value="">choose option</option>
																				: null}
																			<option value="front">Front</option>
																			<option value="back">Back</option>
																		</Form.Control>
																		<br />
																		<br />
																		<Form.Label>Membrane emission rate</Form.Label>
																		<Form.Control type="number" className="form-control" size="sm"
																			name={`compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.membraneEmissionRate`}
																			value={values.compartment[index].annotation["cell4d:compartmentProperties"]._attributes.membraneEmissionRate}
																			onChange={handleChange}
																			onBlur={handleBlur}
																			isValid={getIn(touched, `compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.membraneEmissionRate`) &&
																				!getIn(errors, `compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.membraneEmissionRate`)}
																			isInvalid={getIn(touched, `compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.membraneEmissionRate`) &&
																				!!getIn(errors, `compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.membraneEmissionRate`)} />
																		<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.membraneEmissionRate`))}</Form.Control.Feedback>
																		<br />
																		<Form.Label>Membrane adsorption rate</Form.Label>
																		<Form.Control type="number" className="form-control" size="sm"
																			name={`compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.absorptionRate`}
																			value={values.compartment[index].annotation["cell4d:compartmentProperties"]._attributes.absorptionRate}
																			onChange={handleChange}
																			onBlur={handleBlur}
																			isValid={getIn(touched, `compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.absorptionRate`) &&
																				!getIn(errors, `compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.absorptionRate`)}
																			isInvalid={getIn(touched, `compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.absorptionRate`) &&
																				!!getIn(errors, `compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.absorptionRate`)} />
																		<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `compartment.[${index}].annotation.cell4d:compartmentProperties._attributes.absorptionRate`))}</Form.Control.Feedback>
																		<br />
																		<br />

																	</>
																	:
																	null}
															</Col>
															<Col style={{ paddingRight: 25 }}>
																{showCoords[index] ?
																	<FieldArray name={`compartment.[${index}].annotation.cell4d:latticePointDefinition`} render={arrayHelpers => {
																		return (
																			<div> {
																				values.compartment[index].annotation["cell4d:latticePointDefinition"] && values.compartment[index].annotation["cell4d:latticePointDefinition"].length > 0 ?
																					values.compartment[index].annotation["cell4d:latticePointDefinition"].map((points: any, pIndex: number) => (
																						<div key={pIndex}>
																							<Form.Row>
																								<Col>
																									{pIndex === 0 ? <Form.Label>X-1</Form.Label> : null}
																									<Form.Control className="form-control" size="sm"
																										name={`compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x1`}
																										value={values.compartment[index].annotation["cell4d:latticePointDefinition"][pIndex]["_attributes"].x1}
																										onChange={handleChange}
																										onBlur={handleBlur}
																										isValid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x1`) &&
																											!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x1`)}
																										isInvalid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x1`) &&
																											!!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x1`)} />
																									{/* <Form.Control.Feedback type="invalid">{getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x1`)}</Form.Control.Feedback> */}
																								</Col>
																								<Col>
																									{pIndex === 0 ? <Form.Label>X-2</Form.Label> : null}
																									<Form.Control className="form-control" size="sm"
																										name={`compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x2`}
																										value={values.compartment[index].annotation["cell4d:latticePointDefinition"][pIndex]["_attributes"].x2}
																										onChange={handleChange}
																										onBlur={handleBlur}
																										isValid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x2`) &&
																											!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x2`)}
																										isInvalid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x2`) &&
																											!!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x2`)} />
																									{/* <Form.Control.Feedback type="invalid">{getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].x2`)}</Form.Control.Feedback> */}
																								</Col>
																								<Col>
																									{pIndex === 0 ? <Form.Label>Y-1</Form.Label> : null}
																									<Form.Control className="form-control" size="sm"
																										name={`compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y1`}
																										value={values.compartment[index].annotation["cell4d:latticePointDefinition"][pIndex]["_attributes"].y1}
																										onChange={handleChange}
																										onBlur={handleBlur}
																										isValid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y1`) &&
																											!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y1`)}
																										isInvalid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y1`) &&
																											!!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y1`)} />
																									{/* <Form.Control.Feedback type="invalid">{getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y1`)}</Form.Control.Feedback> */}
																								</Col>
																								<Col>
																									{pIndex === 0 ? <Form.Label>Y-2</Form.Label> : null}
																									<Form.Control className="form-control" size="sm"
																										name={`compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y2`}
																										value={values.compartment[index].annotation["cell4d:latticePointDefinition"][pIndex]["_attributes"].y2}
																										onChange={handleChange}
																										onBlur={handleBlur}
																										isValid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y2`) &&
																											!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y2`)}
																										isInvalid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y2`) &&
																											!!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y2`)} />
																									{/* <Form.Control.Feedback type="invalid">{getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].y2`)}</Form.Control.Feedback> */}
																								</Col>
																								<Col>
																									{pIndex === 0 ? <Form.Label>Z-1</Form.Label> : null}
																									<Form.Control className="form-control" size="sm"
																										name={`compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z1`}
																										value={values.compartment[index].annotation["cell4d:latticePointDefinition"][pIndex]["_attributes"].z1}
																										onChange={handleChange}
																										onBlur={handleBlur}
																										isValid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z1`) &&
																											!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z1`)}
																										isInvalid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z1`) &&
																											!!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z1`)} />
																									{/* <Form.Control.Feedback type="invalid">{getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z1`)}</Form.Control.Feedback> */}
																								</Col>
																								<Col>
																									{pIndex === 0 ? <Form.Label>Z-2</Form.Label> : null}
																									<Form.Control className="form-control" size="sm"
																										name={`compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z2`}
																										value={values.compartment[index].annotation["cell4d:latticePointDefinition"][pIndex]["_attributes"].z2}
																										onChange={handleChange}
																										onBlur={handleBlur}
																										isValid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z2`) &&
																											!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z2`)}
																										isInvalid={getIn(touched, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z2`) &&
																											!!getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z2`)} />
																									{/* <Form.Control.Feedback type="invalid">{getIn(errors, `compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes].z2`)}</Form.Control.Feedback> */}
																								</Col>
																								{/* remove this compartment */}
																								<Col sm={1} className="align-self-end">
																									<Button type="button" size="sm" variant="outline-danger" onClick={() => arrayHelpers.remove(pIndex)}>✕</Button>
																								</Col>
																							</Form.Row>
																							<Row className="justify-content-center custom-error">
																								{showCoordErrors(`compartment.[${index}].annotation.cell4d:latticePointDefinition.[${pIndex}][_attributes]`)}
																							</Row>
																						</div>
																					)) : null
																			}
																				<br />
																				<Button
																					type="button"
																					size="sm"
																					variant="outline-primary"
																					onClick={() => arrayHelpers.push(emptyCompart.annotation["cell4d:latticePointDefinition"][0])}
																					className="justify-content-flex-start">
																					Add coordinates
																	</Button>
																				<br />
																				<br />
																			</div>
																		)
																	}}
																	/>
																	: null}

															</Col>
														</Row>
													</Card>
													<Button type="button" variant="outline-danger" onClick={() => {
														arrayHelpers.remove(index);
														handleCompartCoordsDisplay(index);
													}}>Remove compartment</Button>
													<br />
													<br />
												</div>
											)) : null
										}
											{typeof errors.compartment === "string" ?
												<><div className="alert-danger col-sm justify-content-md-center">{errors.compartment}</div><br /></>
												: null}

											{/* add a new empty compartment at the second last position */}
											<Button type="button" onClick={() => {
												let new_index: number;
												// no matter what, start the real compartments disregarding inaccessible as 1 indexing
												if(values.compartment[0]._attributes.name == "000") {
													new_index = values.compartment.length - 1;
												} else {
													new_index = values.compartment.length;
												}
												let new_compart = JSON.parse(JSON.stringify(emptyCompart));
												let padded_index = String(new_index).padStart(3, "0");
												new_compart._attributes.name = padded_index;
												arrayHelpers.insert(values.compartment.length - 1, new_compart)
												handleCompartCoordsDisplay();
											}}>Add new compartment</Button>
										</div>
									)
								}}
								/>
								<br />

								{/* <Button variant="outline-info">Add Compartment</Button> */}
								<hr />

								<Button type="submit" variant="success" onClick={() => {setSubmitSuccess(false); setTouchedSubmit(true)}}>Save Compartments to model</Button>
								{submitSuccess ? <><br /><br /><div className="alert-success col-lg justify-content-md-center">Compartments saved to model!</div></> : 
											 touchedSubmit ? <><br /><br /><div className="alert-danger col-lg justify-content-md-center">Compartments failed to save.</div></> :
											 null}
								{/* <Row className="text-left">
									<Col>
										Values
									<pre>{JSON.stringify(values, null, 2)}</pre>
									</Col>
									<Col>
										Errors
									<pre>{JSON.stringify(errors, null, 2)}</pre>
									</Col>
									<Col>
										showcoords
									<pre>{JSON.stringify(showCoords, null, 2)}</pre>
									</Col>
								</Row> */}

							</Col></Row>
						</Container>
					</Form>
				)}
			</Formik>
		</div>
	);
}
